// packages/ui/src/TextField.tsx
import React, { useId, InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { AlertCircle, HelpCircle } from 'lucide-react';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  labelKey?: string;
  descriptionText?: string;
  descriptionTextKey?: string;
  error?: string;
  errorText?: string;
  errorTextKey?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  inputClassName?: string;
  labelClassName?: string;
  containerClassName?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Optional text to display in a tooltip next to the label. */
  tooltip?: string;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({
    label,
    labelKey,
    descriptionText,
    descriptionTextKey,
    error,
    errorText,
    errorTextKey,
    leadingIcon,
    trailingIcon,
    containerClassName,
    inputClassName,
    labelClassName,
    size = 'md',
    id,
    type = 'text',
    disabled,
    tooltip,
    ...props
  }, ref) => {
    const { t } = useTranslation();
    const autoId = useId();
    const fieldId = id || autoId;

    const resolvedLabel = labelKey ? t(labelKey) : label;
    const resolvedDescriptionText = descriptionTextKey ? t(descriptionTextKey) : descriptionText;
    const resolvedErrorText = errorTextKey ? t(errorTextKey) : (errorText || error);
    const hasError = !!resolvedErrorText;

    const sizeClasses = {
      sm: 'px-2.5 py-1.5 text-sm rounded-lg [.theme-liquid-glass_&]:rounded-xl',
      md: 'px-3 py-2 text-base rounded-lg [.theme-liquid-glass_&]:rounded-xl',
      lg: 'px-4 py-2.5 text-lg rounded-lg [.theme-liquid-glass_&]:rounded-xl',
    };

    const paddingClasses = clsx({
      'pl-10': leadingIcon,
      'pr-10': trailingIcon || hasError,
    });

    // Label classes with dual-theme support
    const labelClasses = clsx(
      'block text-sm font-medium',
      // Neobrutalism
      'text-gray-700',
      // Liquid glass
      '[.theme-liquid-glass_&]:text-white/90',
      labelClassName
    );

    // Input classes with dual-theme support
    const inputClasses = clsx(
      'block w-full focus:outline-none transition-all duration-150 ease-in-out',

      // Neobrutalism base
      'border-2 bg-white text-gray-900 placeholder-gray-400',
      hasError ? 'border-red-500' : 'border-black',
      !disabled && {
        'hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] hover:-translate-x-px hover:-translate-y-px': !hasError,
        'focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] focus:-translate-x-px focus:-translate-y-px': !hasError,
        'hover:shadow-[2px_2px_0px_0px_rgba(220,38,38,0.9)] hover:-translate-x-px hover:-translate-y-px': hasError,
        'focus:shadow-[2px_2px_0px_0px_rgba(220,38,38,0.9)] focus:-translate-x-px focus:-translate-y-px': hasError,
      },
      hasError && 'text-red-700 placeholder-red-400',

      // Liquid glass overrides
      '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
      '[.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:text-white',
      '[.theme-liquid-glass_&]:placeholder-white/40',
      !disabled && [
        '[.theme-liquid-glass_&]:hover:bg-white/10 [.theme-liquid-glass_&]:hover:shadow-none [.theme-liquid-glass_&]:hover:translate-x-0 [.theme-liquid-glass_&]:hover:translate-y-0',
        '[.theme-liquid-glass_&]:focus:bg-white/10 [.theme-liquid-glass_&]:focus:border-violet-400 [.theme-liquid-glass_&]:focus:shadow-[0_0_0_2px_rgba(139,92,246,0.3)]',
        '[.theme-liquid-glass_&]:focus:translate-x-0 [.theme-liquid-glass_&]:focus:translate-y-0',
      ],
      hasError && '[.theme-liquid-glass_&]:border-rose-400 [.theme-liquid-glass_&]:text-rose-300',

      sizeClasses[size],
      paddingClasses,
      disabled && 'cursor-not-allowed bg-gray-100 opacity-70 border-gray-300 [.theme-liquid-glass_&]:bg-white/5 [.theme-liquid-glass_&]:opacity-50',
      inputClassName
    );

    // Description text classes
    const descriptionClasses = clsx(
      'mt-1.5 text-xs',
      // Neobrutalism
      'text-gray-500',
      // Liquid glass
      '[.theme-liquid-glass_&]:text-white/50'
    );

    // Tooltip classes
    const tooltipClasses = clsx(
      'absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs p-2',
      'text-xs rounded-md shadow-lg',
      'opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none',
      // Neobrutalism
      'text-white bg-gray-800',
      // Liquid glass
      '[.theme-liquid-glass_&]:bg-slate-800/90 [.theme-liquid-glass_&]:backdrop-blur-xl [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20'
    );

    return (
      <div className={clsx('w-full', containerClassName)}>
        {resolvedLabel && (
          <div className="flex items-center mb-1">
            <label htmlFor={fieldId} className={labelClasses}>
              {resolvedLabel}
            </label>
            {tooltip && (
              <div className="relative flex items-center ml-1.5 group">
                <HelpCircle className={clsx(
                  'h-4 w-4 cursor-help',
                  'text-gray-400',
                  '[.theme-liquid-glass_&]:text-white/50'
                )} />
                <div className={tooltipClasses} role="tooltip">
                  {tooltip}
                  <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-800 [.theme-liquid-glass_&]:border-t-slate-800"></div>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="relative">
          {leadingIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 z-10">
              {React.cloneElement(leadingIcon as React.ReactElement, {
                className: clsx(
                  'h-5 w-5',
                  'text-gray-400 [.theme-liquid-glass_&]:text-white/50',
                  (leadingIcon as React.ReactElement).props.className
                )
              })}
            </div>
          )}
          <input
            ref={ref}
            type={type}
            id={fieldId}
            disabled={disabled}
            className={inputClasses}
            aria-invalid={hasError ? 'true' : 'false'}
            aria-describedby={resolvedErrorText ? `${fieldId}-error` : resolvedDescriptionText ? `${fieldId}-description` : undefined}
            {...props}
          />
          {trailingIcon && !hasError && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 z-10">
              {React.cloneElement(trailingIcon as React.ReactElement, {
                className: clsx(
                  'h-5 w-5',
                  'text-gray-400 [.theme-liquid-glass_&]:text-white/50',
                  (trailingIcon as React.ReactElement).props.className
                )
              })}
            </div>
          )}
          {hasError && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 z-10">
              <AlertCircle className="h-5 w-5 text-red-500 [.theme-liquid-glass_&]:text-rose-400" aria-hidden="true" />
            </div>
          )}
        </div>
        {resolvedErrorText && (
          <p id={`${fieldId}-error`} className="mt-1.5 text-xs text-red-600 [.theme-liquid-glass_&]:text-rose-400">
            {resolvedErrorText}
          </p>
        )}
        {resolvedDescriptionText && !hasError && (
          <p id={`${fieldId}-description`} className={descriptionClasses}>
            {resolvedDescriptionText}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';